import { useRef } from "react";
import html2canvas from "html2canvas";
import { Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { track, buildShareUrl } from "@/lib/analytics";
import { useLang } from "@/lib/i18n";

function MiniRing({ value, label }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  const r = 28;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-16 h-16">
        <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
          <circle cx="32" cy="32" r={r} stroke="rgba(255,255,255,0.12)" strokeWidth="4" fill="none" />
          <circle
            cx="32" cy="32" r={r}
            stroke="#D4AF37" strokeWidth="4" strokeLinecap="round" fill="none"
            strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-serif text-lg text-white">
          {pct}
        </div>
      </div>
      <div className="text-[9px] tracking-[0.2em] uppercase text-slate-400">{label}</div>
    </div>
  );
}

export default function ClarityCard({ reading, name, consultationId }) {
  const cardRef = useRef(null);
  const { lang, t } = useLang();

  const clarity = reading.clarity_score ?? 0;
  const emotional = reading.emotional_score ?? Math.max(40, clarity - 5);
  const relationship = reading.relationship_score ?? Math.max(35, clarity - 10);
  // Blind spot may be a structured object ({current_belief, deeper_truth, ...})
  // or a legacy string. Normalize to a single short line for the share card.
  const blindSpotRaw =
    reading.biggest_blind_spot ||
    (reading.hidden_blockers && reading.hidden_blockers[0]) ||
    reading.psychological_analysis;
  const blindSpot =
    typeof blindSpotRaw === "object" && blindSpotRaw
      ? blindSpotRaw.deeper_truth ||
        blindSpotRaw.current_belief ||
        blindSpotRaw.why_it_matters ||
        ""
      : blindSpotRaw || "";
  const action =
    reading.recommended_action ||
    reading.action_plan?.immediate ||
    t("card.fallbackAction");
  const archetypeName = reading?.archetype?.name || "";
  const dateStr = new Date().toLocaleDateString(lang === "hi" ? "hi-IN" : undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const cardUrlDownload = buildShareUrl("download");

  const downloadCard = async (silent = false) => {
    if (!cardRef.current) return null;
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0B1020",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement("a");
      link.download = `mantrana-clarity-card-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      if (!silent) {
        toast.success(t("card.share.saved"));
        track("card_downloaded", {
          utm_source: "download",
          utm_medium: "share",
          utm_campaign: "clarity_card",
          consultation_id: consultationId,
        });
      }
      return canvas;
    } catch (e) {
      console.error(e);
      toast.error(t("card.share.downloadFailed"));
      return null;
    }
  };

  const captionFor = (source) => {
    const url = buildShareUrl(source);
    return t("card.caption", {
      clarity,
      emotional,
      relationship,
      blindSpot,
      action,
      url,
    });
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(captionFor("whatsapp"))}`;
    track("share_whatsapp", {
      utm_source: "whatsapp",
      utm_medium: "share",
      utm_campaign: "clarity_card",
      consultation_id: consultationId,
    });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareInstagram = async () => {
    try {
      await navigator.clipboard.writeText(captionFor("instagram"));
    } catch (_) {
      /* ignore */
    }
    track("share_instagram", {
      utm_source: "instagram",
      utm_medium: "share",
      utm_campaign: "clarity_card",
      consultation_id: consultationId,
    });
    await downloadCard(true);
    toast.success(t("card.share.instaSaved"));
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mt-12" data-testid="clarity-card-section">
      <div className="text-xs tracking-[0.25em] uppercase gold-text mb-3">{t("card.section.eyebrow")}</div>
      <h2 className="font-serif text-3xl md:text-4xl font-light text-white mb-3">
        {t("card.section.title.a")} <span className="italic gold-text">{t("card.section.title.b")}</span>
      </h2>
      <p className="text-slate-400 font-light mb-8 max-w-xl">
        {t("card.section.subtitle")}
      </p>

      <div className="flex justify-center">
        <div
          ref={cardRef}
          className="relative w-full max-w-[460px] rounded-3xl overflow-hidden"
          style={{
            background:
              "linear-gradient(160deg, #0B1020 0%, #13192B 55%, #1a1538 100%)",
            border: "1px solid rgba(212,175,55,0.35)",
            boxShadow: "0 30px 80px rgba(0,0,0,0.5), 0 0 60px rgba(212,175,55,0.15)",
            padding: "44px 36px",
          }}
          data-testid="clarity-card"
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "radial-gradient(1px 1px at 18% 22%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(1.5px 1.5px at 78% 18%, rgba(212,175,55,0.5) 50%, transparent), radial-gradient(1px 1px at 40% 85%, rgba(255,255,255,0.4) 50%, transparent), radial-gradient(1px 1px at 88% 72%, rgba(255,255,255,0.4) 50%, transparent)",
              pointerEvents: "none",
              opacity: 0.7,
            }}
          />
          <div className="relative">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 gold-text" strokeWidth={1.5} />
                <span className="font-serif text-lg text-white">
                  {lang === "hi" ? (
                    <>मंत्रणा<span className="gold-text"> One</span></>
                  ) : (
                    <>Mantrana<span className="gold-text"> One</span></>
                  )}
                </span>
              </div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-slate-400">
                {t("card.header")}
              </div>
            </div>

            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 mb-1">{t("card.for")}</div>
            <div className="font-serif text-2xl text-white mb-3">{name}</div>

            {archetypeName && (
              <div
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-6"
                style={{
                  background: "rgba(212,175,55,0.14)",
                  border: "1px solid rgba(212,175,55,0.4)",
                }}
              >
                <span className="text-[8px] tracking-[0.3em] uppercase gold-text">
                  {t("card.archetype")}
                </span>
                <span className="font-serif italic text-sm text-white">
                  {archetypeName}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between mb-8 pb-8 border-b border-white/8">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-1">
                  {t("card.clarityScore")}
                </div>
                <div className="font-serif text-6xl text-white leading-none">{clarity}</div>
                <div className="text-xs text-slate-400 mt-1">/ 100</div>
              </div>
              <div className="flex gap-5">
                <MiniRing value={emotional} label={t("card.emotional")} />
                <MiniRing value={relationship} label={t("card.relationships")} />
              </div>
            </div>

            <div className="mb-6">
              <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-2">
                {t("card.blindSpot")}
              </div>
              <p className="font-serif text-lg text-white leading-snug">{blindSpot}</p>
            </div>

            <div className="mb-8">
              <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-2">
                {t("card.action")}
              </div>
              <p className="font-light text-slate-200 leading-relaxed">{action}</p>
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-white/8">
              <div className="text-[9px] tracking-[0.25em] uppercase text-slate-500">
                {dateStr}
              </div>
              <div className="text-[9px] tracking-[0.25em] uppercase gold-text text-right">
                mantrana.ai
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={shareWhatsApp}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium border border-white/15 hover:bg-white/5 text-white transition-colors"
          data-testid="share-whatsapp-button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.296-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c.001-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.892a11.82 11.82 0 00-3.48-8.413z"/>
          </svg>
          {t("card.share.whatsapp")}
        </button>
        <button
          onClick={shareInstagram}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium border border-white/15 hover:bg-white/5 text-white transition-colors"
          data-testid="share-instagram-button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="url(#ig-grad)" strokeWidth="2" aria-hidden="true">
            <defs>
              <linearGradient id="ig-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#feda75"/>
                <stop offset="50%" stopColor="#d62976"/>
                <stop offset="100%" stopColor="#4f5bd5"/>
              </linearGradient>
            </defs>
            <rect x="2" y="2" width="20" height="20" rx="5"/>
            <circle cx="12" cy="12" r="4"/>
            <circle cx="17.5" cy="6.5" r="1" fill="url(#ig-grad)" stroke="none"/>
          </svg>
          {t("card.share.instagram")}
        </button>
        <button
          onClick={() => downloadCard(false)}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium gold-glow"
          style={{ background: "#D4AF37", color: "#0B1020" }}
          data-testid="download-card-button"
        >
          <Download className="w-4 h-4" />
          {t("card.share.download")}
        </button>
      </div>
      <p className="mt-4 text-center text-xs text-slate-500">
        {t("card.share.privacy")}
      </p>
      {/* hidden anchor used as link sink only for download tagging */}
      <span aria-hidden="true" className="hidden">{cardUrlDownload}</span>
    </div>
  );
}
